// src/blocks/Hero.tsx
import { Link } from "react-router-dom";

// src/components/ui/button.tsx
import * as React from "react";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function isNativeHref(href) {
  return href.startsWith("http") || href.startsWith("#") || href.startsWith("mailto:");
}

// src/components/ui/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8 text-base",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "button",
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/blocks/Hero.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function Hero({
  title = "Welcome",
  subtitle = "Build something great.",
  ctaText = "Get started",
  ctaHref = "/",
  className
}) {
  const isNative = isNativeHref(ctaHref);
  const isExternal = ctaHref.startsWith("http");
  return /* @__PURE__ */ jsx2(
    "section",
    {
      className: cn(
        "relative w-full py-24 px-6 md:py-32 lg:py-40",
        "bg-background text-foreground",
        className
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "mx-auto max-w-3xl text-center space-y-8", children: [
        /* @__PURE__ */ jsx2("h1", { className: "text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-foreground", children: title }),
        subtitle && /* @__PURE__ */ jsx2("p", { className: "text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto", children: subtitle }),
        ctaText && /* @__PURE__ */ jsx2("div", { className: "flex flex-wrap gap-4 justify-center", children: isNative ? /* @__PURE__ */ jsx2(
          "a",
          {
            href: ctaHref,
            ...isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {},
            children: /* @__PURE__ */ jsx2(Button, { size: "lg", className: "text-base px-8", children: ctaText })
          }
        ) : /* @__PURE__ */ jsx2(Link, { to: ctaHref, children: /* @__PURE__ */ jsx2(Button, { size: "lg", className: "text-base px-8", children: ctaText }) }) })
      ] })
    }
  );
}

// src/components/ui/card.tsx
import * as React2 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Card = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border border-border bg-card text-card-foreground shadow",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "h3",
  {
    ref,
    className: cn("font-semibold leading-none tracking-tight", className),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx3(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/blocks/FeatureGrid.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var defaultItems = [
  { title: "Fast", description: "Lightning-fast performance." },
  { title: "Simple", description: "Easy to use and customize." },
  { title: "Flexible", description: "Full control over your site." }
];
function FeatureGrid({
  heading = "Features",
  items = defaultItems,
  id,
  className
}) {
  return /* @__PURE__ */ jsx4(
    "section",
    {
      id,
      className: cn(
        "w-full py-16 px-6 bg-muted/30",
        className
      ),
      children: /* @__PURE__ */ jsxs2("div", { className: "mx-auto max-w-6xl", children: [
        /* @__PURE__ */ jsx4("h2", { className: "text-3xl font-bold text-center text-foreground mb-12", children: heading }),
        /* @__PURE__ */ jsx4("div", { className: "grid gap-6 sm:grid-cols-2 lg:grid-cols-3", children: items.map((item, i) => /* @__PURE__ */ jsx4(Card, { className: "border-border bg-card", children: /* @__PURE__ */ jsxs2(CardHeader, { children: [
          /* @__PURE__ */ jsx4(CardTitle, { className: "text-foreground", children: item.title }),
          item.description && /* @__PURE__ */ jsx4(CardDescription, { children: item.description })
        ] }) }, i)) })
      ] })
    }
  );
}

// src/blocks/CTA.tsx
import { Link as Link2 } from "react-router-dom";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
function CTA({
  title = "Ready to get started?",
  buttonText = "Contact us",
  buttonHref = "/contact",
  className
}) {
  const isNative = isNativeHref(buttonHref);
  const isExternal = buttonHref.startsWith("http");
  return /* @__PURE__ */ jsx5(
    "section",
    {
      className: cn(
        "w-full py-20 px-6 bg-primary text-primary-foreground",
        className
      ),
      children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto max-w-2xl text-center space-y-8", children: [
        /* @__PURE__ */ jsx5("h2", { className: "text-3xl font-bold tracking-tight md:text-4xl", children: title }),
        isNative ? /* @__PURE__ */ jsx5(
          "a",
          {
            href: buttonHref,
            ...isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {},
            children: /* @__PURE__ */ jsx5(
              Button,
              {
                size: "lg",
                variant: "secondary",
                className: "bg-primary-foreground text-primary hover:bg-primary-foreground/90",
                children: buttonText
              }
            )
          }
        ) : /* @__PURE__ */ jsx5(Link2, { to: buttonHref, children: /* @__PURE__ */ jsx5(
          Button,
          {
            size: "lg",
            variant: "secondary",
            className: "bg-primary-foreground text-primary hover:bg-primary-foreground/90",
            children: buttonText
          }
        ) })
      ] })
    }
  );
}

// src/blocks/Footer.tsx
import { Link as Link3 } from "react-router-dom";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function Footer({
  copyright = "\xA9 2025 My App. All rights reserved.",
  links = [],
  className
}) {
  return /* @__PURE__ */ jsx6(
    "footer",
    {
      className: cn(
        "w-full py-12 px-6 border-t border-border bg-muted/30",
        className
      ),
      children: /* @__PURE__ */ jsxs4("div", { className: "mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-6", children: [
        /* @__PURE__ */ jsx6("p", { className: "text-sm text-muted-foreground", children: copyright }),
        links.length > 0 && /* @__PURE__ */ jsx6("nav", { className: "flex flex-wrap gap-6", "aria-label": "Footer", children: links.map((link, i) => {
          const isNative = isNativeHref(link.href);
          const isExternal = link.href.startsWith("http");
          return isNative ? /* @__PURE__ */ jsx6(
            "a",
            {
              href: link.href,
              ...isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {},
              className: "text-sm text-muted-foreground hover:text-foreground transition-colors",
              children: link.label
            },
            i
          ) : /* @__PURE__ */ jsx6(
            Link3,
            {
              to: link.href,
              className: "text-sm text-muted-foreground hover:text-foreground transition-colors",
              children: link.label
            },
            i
          );
        }) })
      ] })
    }
  );
}

// src/blocks/Navbar.tsx
import { Link as Link4 } from "react-router-dom";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function Navbar({
  siteName = "Site",
  homeHref = "/",
  links = [],
  className
}) {
  return /* @__PURE__ */ jsx7(
    "header",
    {
      className: cn(
        "w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        className
      ),
      children: /* @__PURE__ */ jsxs5("div", { className: "mx-auto max-w-6xl px-6 h-16 flex items-center justify-between", children: [
        isNativeHref(homeHref) ? /* @__PURE__ */ jsx7(
          "a",
          {
            href: homeHref,
            className: "text-lg font-semibold text-foreground hover:text-primary transition-colors",
            children: siteName
          }
        ) : /* @__PURE__ */ jsx7(
          Link4,
          {
            to: homeHref,
            className: "text-lg font-semibold text-foreground hover:text-primary transition-colors",
            children: siteName
          }
        ),
        /* @__PURE__ */ jsx7("nav", { className: "flex items-center gap-6", "aria-label": "Main", children: links.map((link, i) => {
          const isNative = isNativeHref(link.href);
          const isExternal = link.href.startsWith("http");
          return isNative ? /* @__PURE__ */ jsx7(
            "a",
            {
              href: link.href,
              ...isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {},
              className: "text-sm font-medium text-muted-foreground hover:text-foreground transition-colors",
              children: link.label
            },
            i
          ) : /* @__PURE__ */ jsx7(
            Link4,
            {
              to: link.href,
              className: "text-sm font-medium text-muted-foreground hover:text-foreground transition-colors",
              children: link.label
            },
            i
          );
        }) })
      ] })
    }
  );
}

// src/index.tsx
var blockMap = {
  hero: Hero,
  featureGrid: FeatureGrid,
  cta: CTA,
  footer: Footer,
  navbar: Navbar
};
export {
  Button,
  CTA,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  FeatureGrid,
  Footer,
  Hero,
  Navbar,
  blockMap,
  buttonVariants
};
//# sourceMappingURL=index.js.map